import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { fetchOrderById } from '../../../../store/orders/orderReducer';
import Spinner from '../../../../components/Spinner/Index';

const AdminViewOrders = () => {
    const { orderId } = useParams();
    const dispatch = useDispatch();
    const token = useSelector((state) => state.auth.token);
    const { order, isLoading, isError, message } = useSelector((state) => state.orders);

    useEffect(() => {
        if (orderId && token) {
            dispatch(fetchOrderById({ orderId, token }));
        }
    }, [dispatch, orderId, token]);

    if (isLoading) return <Spinner />;
    if (isError) return <div className="text-red-500 text-center">{message}</div>;
    if (!order) return null;

    return (
        <div className="space-y-6 p-6 bg-gray-50">
            {/* General Order Details Card */}
            <div className="bg-white shadow sm:rounded-lg">
                <div className="px-4 py-6 sm:px-6">
                    <h3 className="text-lg font-semibold text-gray-900">Order Summary</h3>
                    <p className="mt-1 text-sm text-gray-500">Details about your order.</p>
                </div>
                <div className="border-t border-gray-100">
                    <dl className="divide-y divide-gray-100">
                        <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt className="text-sm font-medium text-gray-900">Order ID</dt>
                            <dd className="mt-1 text-sm text-gray-700 sm:col-span-2">{order._id}</dd>
                        </div>
                        <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt className="text-sm font-medium text-gray-900">Total Amount</dt>
                            <dd className="mt-1 text-sm text-gray-700 sm:col-span-2">${order.totalAmount.toFixed(2)}</dd>
                        </div>
                        <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt className="text-sm font-medium text-gray-900">Payment Status</dt>
                            <dd className="mt-1 text-sm text-gray-700 sm:col-span-2">{order.paymentStatus}</dd>
                        </div>
                        <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt className="text-sm font-medium text-gray-900">Receipt</dt>
                            <dd className="mt-1 text-sm text-gray-700 sm:col-span-2">
                                {order.receiptUrl ? (
                                    <a
                                        href={order.receiptUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-indigo-600 hover:text-indigo-500"
                                    >
                                        View Receipt
                                    </a>
                                ) : (
                                    'No receipt available'
                                )}
                            </dd>
                        </div>
                        <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt className="text-sm font-medium text-gray-900">Payment Method</dt>
                            <dd className="mt-1 text-sm text-gray-700 sm:col-span-2">
                                <p><strong>Type:</strong> {order.paymentMethod?.toUpperCase() || 'N/A'}</p>
                                <p><strong>Brand:</strong> {order.cardDetails?.brand?.toUpperCase() || 'N/A'}</p>
                                <p><strong>Last 4 Digits:</strong> {order.cardDetails?.last4 || 'N/A'}</p>
                                <p><strong>Expiry:</strong> {order.cardDetails?.expMonth}/{order.cardDetails?.expYear}</p>
                            </dd>
                        </div>
                    </dl>
                </div>
            </div>

            {/* Items and Business Details Cards */}
            {order.items.map((item, index) => {
                const business = item.businessId?.businessInformation || {};
                const categories = item.businessId?.categoriesInformation || {};
                const workingHours = item.businessId?.workingHours || {};
                const mediaInfo = item.businessId?.mediaInformation || {};

                return (
                    <div key={index} className="bg-white shadow sm:rounded-lg">
                        <div className="px-4 py-6 sm:px-6">
                            <h3 className="text-lg font-semibold text-gray-900">Item Details</h3>
                        </div>
                        <div className="border-t border-gray-100">
                            <dl className="divide-y divide-gray-100">
                                {/* Product Details */}
                                <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                    <dt className="text-sm font-medium text-gray-900">Product</dt>
                                    <dd className="mt-1 text-sm text-gray-700 sm:col-span-2">
                                        <div className="flex items-center space-x-4">
                                            <img
                                                src={`/public/${item.product.image_url}`}
                                                alt={item.product.name}
                                                className="h-16 w-16 rounded-md object-cover"
                                            />
                                            <div>
                                                <p className="text-sm font-bold text-gray-900">{item.product.name}</p>
                                                <p className="text-sm text-gray-500">Package: {item.packageName}</p>
                                                <p className="text-sm text-gray-500">Quantity: {item.quantity}</p>
                                                <p className="text-sm text-gray-900">Price: ${item.packagePrice}</p>
                                            </div>
                                        </div>
                                    </dd>
                                </div>

                                {/* Business Details */}
                                <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                    <dt className="text-sm font-medium text-gray-900">Business</dt>
                                    <dd className="mt-1 text-sm text-gray-700 sm:col-span-2">
                                        <p><strong>Name:</strong> {business.businessName || 'N/A'}</p>
                                        <p><strong>Owner:</strong> {business.ownerName || 'N/A'}</p>
                                        <p><strong>Email:</strong> {business.businessEmail || 'N/A'}</p>
                                        <p><strong>Phone:</strong> {business.phone || 'N/A'}</p>
                                        <p><strong>Address:</strong> {business.address?.line1}, {business.address?.city}, {business.address?.state}, {business.address?.zipCode}</p>
                                    </dd>
                                </div>

                                {/* Categories */}
                                <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                    <dt className="text-sm font-medium text-gray-900">Categories</dt>
                                    <dd className="mt-1 text-sm text-gray-700 sm:col-span-2">
                                        <p><strong>Category:</strong> {categories.businessCategory || 'N/A'}</p>
                                        <p><strong>Subcategory:</strong> {categories.subcategory || 'N/A'}</p>
                                        <p><strong>Description:</strong> {categories.description || 'N/A'}</p>
                                    </dd>
                                </div>

                                {/* Working Hours */}
                                <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                    <dt className="text-sm font-medium text-gray-900">Working Hours</dt>
                                    <dd className="mt-1 text-sm text-gray-700 sm:col-span-2">
                                        {Object.entries(workingHours).map(([day, slots]) => (
                                            <div key={day}>
                                                <strong>{day.charAt(0).toUpperCase() + day.slice(1)}:</strong>{' '}
                                                {Array.isArray(slots) && slots.length > 0
                                                    ? slots.map((slot) => `${slot.openHours}:${slot.openMinutes} - ${slot.closeHours}:${slot.closeMinutes}`).join(', ')
                                                    : 'Closed'}
                                            </div>
                                        ))}
                                    </dd>
                                </div>

                                {/* Media Information */}
                                <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                    <dt className="text-sm font-medium text-gray-900">Media</dt>
                                    <dd className="mt-1 text-sm text-gray-700 sm:col-span-2">
                                        <ul>
                                            {mediaInfo.uploadedImages?.map((url, index) => (
                                                <li key={index}>
                                                    <a
                                                        href={url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-indigo-600 hover:text-indigo-500"
                                                    >
                                                        View Image {index + 1}
                                                    </a>
                                                </li>
                                            ))}
                                        </ul>
                                    </dd>
                                </div>
                            </dl>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default AdminViewOrders;
